import React, { useState, useEffect } from 'react';
import {
    Snackbar,
    Alert,
    Button,
    Box,
    Typography,
} from '@mui/material';
import {
    Share as ShareIcon,
    Close as CloseIcon,
    Celebration as CelebrationIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import FlexCardShareDialog from './FlexCardShareDialog';
import { trackFlexEvent, FLEX_EVENTS } from '../../utils/flexAnalytics';

export default function FlexCardPrompt({ eligibility, sessionId }) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [generatedCard, setGeneratedCard] = useState(null);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        if (eligibility?.eligible && eligibility.reasons?.length > 0) {
            setShowPrompt(true);
            trackFlexEvent(FLEX_EVENTS.CARD_ELIGIBLE, {
                reasons: eligibility.reasons.map(r => r.type),
                session_id: sessionId,
            });
        }
    }, [eligibility, sessionId]);

    const handleGenerate = async (reason) => {
        setGenerating(true);
        try {
            let cardType = 'session';
            let extraData = {};

            if (reason.type === 'streak_milestone') {
                cardType = 'streak';
                extraData = {};
            } else if (reason.type === 'badge_unlock') {
                cardType = 'badge';
                extraData = {
                    badgeName: reason.badgeName,
                    badgeCategory: reason.badgeCategory,
                    badgeRarity: reason.badgeRarity,
                };
            } else {
                cardType = 'session';
                extraData = { sessionId };
            }

            const res = await api.post('/flex/cards/generate', {
                cardType,
                aspectRatio: '1:1',
                ...extraData,
            });

            setGeneratedCard(res.data);
            setShowPrompt(false);
            setShareDialogOpen(true);
            trackFlexEvent(FLEX_EVENTS.CARD_GENERATED, {
                card_id: res.data.cardId,
                card_type: cardType,
                reason: reason.type,
            });
        } catch (err) {
            console.error('FLEX card generation error:', err);
        } finally {
            setGenerating(false);
        }
    };

    const handleClose = () => {
        setShowPrompt(false);
    };

    if (!eligibility?.eligible) return null;

    const primaryReason = eligibility.reasons[0];

    return (
        <>
            <Snackbar
                open={showPrompt}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': { width: '100%' },
                }}
            >
                <Alert
                    severity="success"
                    icon={<CelebrationIcon />}
                    sx={{
                        width: '100%',
                        alignItems: 'flex-start',
                        '& .MuiAlert-message': { width: '100%' },
                    }}
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                color="inherit"
                                size="small"
                                startIcon={<ShareIcon />}
                                onClick={() => handleGenerate(primaryReason)}
                                disabled={generating}
                            >
                                {generating ? 'Creating...' : 'Share'}
                            </Button>
                            <CloseIcon
                                onClick={handleClose}
                                sx={{ cursor: 'pointer', opacity: 0.6, fontSize: 20 }}
                            />
                        </Box>
                    }
                >
                    <Typography variant="subtitle2" fontWeight={700}>
                        {primaryReason.headline}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {primaryReason.description}
                    </Typography>
                    {eligibility.reasons.length > 1 && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
                            +{eligibility.reasons.length - 1} more achievement{eligibility.reasons.length > 2 ? 's' : ''} to share!
                        </Typography>
                    )}
                </Alert>
            </Snackbar>

            <FlexCardShareDialog
                open={shareDialogOpen}
                card={generatedCard}
                onClose={() => setShareDialogOpen(false)}
            />
        </>
    );
}
